import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Checkbox} from '@/components/ui/checkbox'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {MunicipioPicker} from '@/components/municipio-picker'
import {HelpIcon} from '@/components/help-icon'
import {CropImage, OPCION_BASE, OPCION_ON, OPCION_OFF} from '@/components/bateria/tira-opciones'
import {
  useFicha,
  useFichaOcr,
  useSaveFicha,
  type BateriaFicha,
  type FichaCampoMeta,
  type FichaCampoValor,
  type FichaUpsert,
} from '@/hooks/bateria/use-bateria'
import {translateApiError} from '@/lib/errors'
import {useRecorteVivo} from '@/lib/recortes-caidos'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

// Numbers travel as strings while editing and are coerced on save.
type Draft = Record<string, string | boolean>
type SetDraft = (updater: (prev: Draft) => Draft) => void

function draftFromCampos(catalogo: FichaCampoMeta[], campos: Record<string, FichaCampoValor>): Draft {
  const draft: Draft = {}
  for (const meta of catalogo) {
    const value = campos[meta.id]?.value ?? null
    draft[meta.id] = meta.tipo === 'booleano' ? value === true : value == null ? '' : String(value)
  }
  return draft
}

// An emptied text field becomes null (unset), never "".
function upsertFromDraft(catalogo: FichaCampoMeta[], draft: Draft): FichaUpsert {
  const body: Record<string, string | number | boolean | null> = {}
  for (const meta of catalogo) {
    const raw = draft[meta.id]
    if (meta.tipo === 'booleano') body[meta.id] = raw === true
    else if (meta.tipo === 'numero') body[meta.id] = raw === '' ? null : Number(raw)
    else body[meta.id] = raw === '' ? null : String(raw)
  }
  return body as FichaUpsert
}

// En el papel es una tabla de dos filas y una sola pregunta (spec §3.8): marcar la fila de abajo sin
// número es la tercera respuesta, «≥1 año, sin especificar».
const ANTIGUEDAD_PARES: {menos1: string; anios: string; mas1: string; labelKey: string}[] = [
  {
    menos1: 'antiguedad_empresa_menos_1_anio',
    anios: 'antiguedad_empresa_anios',
    mas1: 'antiguedad_empresa_mas_1_anio',
    labelKey: 'antiguedad_empresa',
  },
  {
    menos1: 'antiguedad_cargo_menos_1_anio',
    anios: 'antiguedad_cargo_anios',
    mas1: 'antiguedad_cargo_mas_1_anio',
    labelKey: 'antiguedad_cargo',
  },
]

type PlanItem =
  | {kind: 'campo'; meta: FichaCampoMeta}
  | {
      kind: 'antiguedad'
      metaMenos1: FichaCampoMeta
      metaAnios: FichaCampoMeta
      metaMas1: FichaCampoMeta
      labelKey: string
    }

/** Fusiona los tríos de antigüedad buscando por id, así un catálogo reordenado no rompe la fusión. */
function construirPlan(catalogo: FichaCampoMeta[]): PlanItem[] {
  const porId = new Map(catalogo.map((m) => [m.id, m]))
  const consumidos = new Set<string>()
  const plan: PlanItem[] = []
  for (const meta of catalogo) {
    if (consumidos.has(meta.id)) continue
    const par = ANTIGUEDAD_PARES.find((p) => p.menos1 === meta.id || p.anios === meta.id || p.mas1 === meta.id)
    if (par) {
      consumidos.add(par.menos1)
      consumidos.add(par.anios)
      consumidos.add(par.mas1)
      const metaMenos1 = porId.get(par.menos1)
      const metaAnios = porId.get(par.anios)
      const metaMas1 = porId.get(par.mas1)
      if (metaMenos1 && metaAnios && metaMas1)
        plan.push({kind: 'antiguedad', metaMenos1, metaAnios, metaMas1, labelKey: par.labelKey})
      continue
    }
    plan.push({kind: 'campo', meta})
  }
  return plan
}

// Sale de `alimenta_informe`, que el backend deriva de lo que consume el motor (spec §3.2).
function itemAlimentaInforme(item: PlanItem, campos: Record<string, FichaCampoValor>): boolean {
  if (item.kind === 'campo') return campos[item.meta.id]?.alimenta_informe ?? false
  return Boolean(
    campos[item.metaMenos1.id]?.alimenta_informe ||
    campos[item.metaAnios.id]?.alimenta_informe ||
    campos[item.metaMas1.id]?.alimenta_informe
  )
}

function OrigenTag({campo, fieldKey}: {campo: FichaCampoValor | undefined; fieldKey: string}) {
  const {t} = useTranslation()
  const origen = campo?.origen ?? 'vacio'
  if (origen === 'guardado')
    return (
      <Tag tone="green" data-testid={`ficha-confirmado-${fieldKey}`}>
        {t('admin.bateria.ficha.confirmado')}
      </Tag>
    )
  if (origen === 'omr')
    return campo?.needs_review ? (
      <Tag tone="amber" data-testid={`ficha-revisar-${fieldKey}`}>
        {t('admin.bateria.ficha.revisar')}
      </Tag>
    ) : (
      <Tag tone="neutral" data-testid={`ficha-leido-${fieldKey}`}>
        {t('admin.bateria.ficha.leidoOmr')}
      </Tag>
    )
  if (origen === 'ocr')
    return (
      <Tag tone="neutral" data-testid={`ficha-ocr-${fieldKey}`}>
        {t('admin.bateria.ficha.leidoIa')}
      </Tag>
    )
  if (origen === 'kairos')
    return (
      <Tag tone="neutral" data-testid={`ficha-kairos-${fieldKey}`}>
        {t('admin.bateria.ficha.sugeridoKairos')}
      </Tag>
    )
  return null
}

// OMR dudoso y lectura de IA comparten el borde ámbar del control; el tag solo dice la procedencia.
function necesitaAtencion(campo: FichaCampoValor | undefined): boolean {
  if (!campo) return false
  return (campo.origen === 'omr' && campo.needs_review) || campo.origen === 'ocr'
}

function Recorte({url, mostrarDegradado, testId}: {url: string | null; mostrarDegradado: boolean; testId: string}) {
  const {t} = useTranslation()
  // Vivo y no el crudo: el lifecycle borra estos PNG a los 30 días y acá no hay peldaño debajo.
  const vivo = useRecorteVivo(url)
  if (vivo) {
    return (
      <CropImage
        url={vivo}
        alt={t('admin.bateria.ficha.recorteAlt')}
        fallback={t('admin.bateria.ficha.sinRecorte')}
        testId={testId}
      />
    )
  }
  if (!mostrarDegradado) return null
  return (
    <p
      className="rounded-md border border-dashed border-border px-2.5 py-1.5 text-xs text-muted-foreground"
      data-testid={`${testId}-degradado`}>
      {t('admin.bateria.ficha.sinRecorte')}
    </p>
  )
}

function CampoUnico({
  meta,
  campo,
  value,
  onChange,
}: {
  meta: FichaCampoMeta
  campo: FichaCampoValor | undefined
  value: string | boolean
  onChange: (next: string | boolean) => void
}) {
  const {t} = useTranslation()
  const atencion = necesitaAtencion(campo)
  const label = t(`admin.bateria.ficha.campo.${meta.id}`)

  const control =
    meta.tipo === 'booleano' ? (
      <label className="flex items-center gap-2 text-sm text-foreground">
        <Checkbox
          checked={value === true}
          onCheckedChange={onChange}
          data-testid={`ficha-campo-${meta.id}`}
          aria-label={label}
        />
        <span>{t('admin.bateria.ficha.si')}</span>
      </label>
    ) : meta.tipo === 'casilla' ? (
      <select
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`ficha-campo-${meta.id}`}
        aria-label={label}
        aria-invalid={atencion || undefined}
        className={`h-9 w-full rounded-lg border bg-transparent px-2.5 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
          atencion ? 'border-tone-amber-fg' : 'border-input'
        }`}>
        <option value="">{t('admin.bateria.ficha.sinSeleccion')}</option>
        {meta.opciones.map((opcion) => (
          <option key={opcion} value={opcion}>
            {t(`admin.bateria.ficha.opcion.${meta.id}.${opcion}`)}
          </option>
        ))}
      </select>
    ) : (
      <Input
        maxLength={tope('NOTA')}
        type={meta.tipo === 'numero' ? 'number' : 'text'}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`ficha-campo-${meta.id}`}
        aria-label={label}
        aria-invalid={atencion || undefined}
        className={atencion ? 'border-tone-amber-fg' : undefined}
      />
    )

  return (
    <div className="space-y-1.5" data-testid={`ficha-field-${meta.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <OrigenTag campo={campo} fieldKey={meta.id} />
      </div>
      <Recorte
        url={campo?.crop_download_url ?? null}
        mostrarDegradado={campo?.alimenta_informe ?? false}
        testId={`ficha-crop-${meta.id}`}
      />
      {control}
    </div>
  )
}

/** Una sola respuesta a la vez: elegir un botón vacía el número y apaga el otro (spec §3.8). */
function CampoAntiguedad({
  metaMenos1,
  metaAnios,
  metaMas1,
  labelKey,
  campoMenos1,
  campoAnios,
  campoMas1,
  valueMenos1,
  valueAnios,
  valueMas1,
  onChangeMenos1,
  onChangeAnios,
  onChangeMas1,
}: {
  metaMenos1: FichaCampoMeta
  metaAnios: FichaCampoMeta
  metaMas1: FichaCampoMeta
  labelKey: string
  campoMenos1: FichaCampoValor | undefined
  campoAnios: FichaCampoValor | undefined
  campoMas1: FichaCampoValor | undefined
  valueMenos1: boolean
  valueAnios: string
  valueMas1: boolean
  onChangeMenos1: (next: boolean) => void
  onChangeAnios: (next: string) => void
  onChangeMas1: (next: boolean) => void
}) {
  const {t} = useTranslation()
  const label = t(`admin.bateria.ficha.campo.${labelKey}`)
  const alimenta = Boolean(campoMenos1?.alimenta_informe || campoAnios?.alimenta_informe)
  const cropUrl =
    campoMenos1?.crop_download_url ?? campoMas1?.crop_download_url ?? campoAnios?.crop_download_url ?? null
  const campoActivo = valueMenos1 ? campoMenos1 : valueMas1 ? campoMas1 : campoAnios

  function elegir(cual: 'menos' | 'mas') {
    const next = cual === 'menos' ? !valueMenos1 : !valueMas1
    onChangeMenos1(cual === 'menos' ? next : false)
    onChangeMas1(cual === 'mas' ? next : false)
    if (next) onChangeAnios('')
  }

  return (
    <div className="space-y-1.5" data-testid={`ficha-field-${metaAnios.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <OrigenTag campo={campoActivo} fieldKey={metaAnios.id} />
      </div>
      <Recorte url={cropUrl} mostrarDegradado={alimenta} testId={`ficha-crop-${metaAnios.id}`} />
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="number"
          value={valueMenos1 || valueMas1 ? '' : valueAnios}
          disabled={valueMenos1 || valueMas1}
          onChange={(e) => onChangeAnios(e.target.value)}
          data-testid={`ficha-campo-${metaAnios.id}`}
          aria-label={t(`admin.bateria.ficha.campo.${metaAnios.id}`)}
          className="max-w-28"
        />
        <button
          type="button"
          onClick={() => elegir('menos')}
          aria-pressed={valueMenos1}
          data-testid={`ficha-campo-${metaMenos1.id}`}
          className={cn(OPCION_BASE, valueMenos1 ? OPCION_ON : OPCION_OFF)}>
          {t('admin.bateria.ficha.menosDeUnAnio')}
        </button>
        <button
          type="button"
          onClick={() => elegir('mas')}
          aria-pressed={valueMas1}
          data-testid={`ficha-campo-${metaMas1.id}`}
          className={cn(OPCION_BASE, valueMas1 ? OPCION_ON : OPCION_OFF)}>
          {t('admin.bateria.ficha.masDeUnAnio')}
        </button>
      </div>
    </div>
  )
}

// Espejo de `core/bateria_ficha.py::MUNICIPIOS_DE_LA_FICHA`.
const CIUDADES_DE_LA_FICHA = {
  residencia_ciudad: 'residencia_municipio_codigo',
  trabajo_ciudad: 'trabajo_municipio_codigo',
} as const

/** Ciudad escrita que no llegó al catálogo DIVIPOLA: elegirla reemplaza el texto por el nombre
 * canónico y el código se resuelve al guardar. */
function CiudadPorConfirmar({
  businessId,
  campoId,
  ficha,
  draft,
  setDraft,
}: {
  businessId: string
  campoId: keyof typeof CIUDADES_DE_LA_FICHA
  ficha: BateriaFicha
  draft: Draft
  setDraft: SetDraft
}) {
  const {t} = useTranslation()
  const guardado = ficha.campos[campoId]?.value
  const escrito = String(draft[campoId] ?? '')
  const resuelto = ficha[CIUDADES_DE_LA_FICHA[campoId]]
  if (!escrito.trim() || resuelto || escrito !== String(guardado ?? '')) return null
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`ficha-${campoId}-por-confirmar`}>
      <Tag tone="amber">{t('documento.porConfirmar')}</Tag>
      <div className="min-w-56 flex-1">
        <MunicipioPicker
          businessId={businessId}
          value={null}
          disabled={false}
          onPick={(municipio) => setDraft((prev) => ({...prev, [campoId]: municipio.nombre}))}
        />
      </div>
    </div>
  )
}

function renderPlanItem(item: PlanItem, ficha: BateriaFicha, draft: Draft, setDraft: SetDraft, businessId: string) {
  const campos = ficha.campos
  if (item.kind === 'campo' && item.meta.id in CIUDADES_DE_LA_FICHA) {
    const campoId = item.meta.id as keyof typeof CIUDADES_DE_LA_FICHA
    return (
      <div key={item.meta.id} className="space-y-2">
        <CampoUnico
          meta={item.meta}
          campo={campos[item.meta.id]}
          value={draft[item.meta.id] ?? ''}
          onChange={(next) => setDraft((prev) => ({...prev, [item.meta.id]: next}))}
        />
        <CiudadPorConfirmar businessId={businessId} campoId={campoId} ficha={ficha} draft={draft} setDraft={setDraft} />
      </div>
    )
  }
  if (item.kind === 'campo') {
    return (
      <CampoUnico
        key={item.meta.id}
        meta={item.meta}
        campo={campos[item.meta.id]}
        value={draft[item.meta.id] ?? (item.meta.tipo === 'booleano' ? false : '')}
        onChange={(next) => setDraft((prev) => ({...prev, [item.meta.id]: next}))}
      />
    )
  }
  return (
    <CampoAntiguedad
      key={item.metaAnios.id}
      metaMenos1={item.metaMenos1}
      metaAnios={item.metaAnios}
      metaMas1={item.metaMas1}
      labelKey={item.labelKey}
      campoMenos1={campos[item.metaMenos1.id]}
      campoAnios={campos[item.metaAnios.id]}
      campoMas1={campos[item.metaMas1.id]}
      valueMenos1={draft[item.metaMenos1.id] === true}
      valueAnios={typeof draft[item.metaAnios.id] === 'string' ? (draft[item.metaAnios.id] as string) : ''}
      valueMas1={draft[item.metaMas1.id] === true}
      onChangeMenos1={(next) => setDraft((prev) => ({...prev, [item.metaMenos1.id]: next}))}
      onChangeAnios={(next) => setDraft((prev) => ({...prev, [item.metaAnios.id]: next}))}
      onChangeMas1={(next) => setDraft((prev) => ({...prev, [item.metaMas1.id]: next}))}
    />
  )
}

type OcrEstado = {tono: 'exito' | 'aviso'; texto: string}

function ocrErrorDetail(err: unknown): string | undefined {
  return (err as {response?: {data?: {detail?: string}}} | null)?.response?.data?.detail
}

/** OCR manual y relanzable, nunca automático: cuesta una llamada paga. Un fallo del modelo vuelve
 * 200 con `campos_leidos=0` y un motivo. */
function OcrTrigger({businessId, paqueteId}: {businessId: string; paqueteId: string}) {
  const {t} = useTranslation()
  const ocr = useFichaOcr(businessId, paqueteId)
  const [estado, setEstado] = useState<OcrEstado | null>(null)

  function lanzar() {
    setEstado(null)
    ocr.mutate(undefined, {
      onSuccess: (data) => {
        setEstado(
          data.campos_leidos > 0
            ? {tono: 'exito', texto: t('admin.bateria.ficha.ocr.exito', {count: data.campos_leidos})}
            : {
                tono: 'aviso',
                texto: t('admin.bateria.ficha.ocr.sinLectura', {
                  detail: data.error ?? t('admin.bateria.ficha.ocr.sinMotivo'),
                }),
              }
        )
      },
      onError: (err) => {
        const detail = ocrErrorDetail(err)
        const texto =
          detail === 'the paquete has no canonical PDF yet'
            ? t('admin.bateria.ficha.ocr.errorSinPdf')
            : detail === 'the evaluación is closed and can no longer be modified'
              ? t('admin.bateria.ficha.ocr.errorCerrada')
              : translateApiError(err)
        setEstado({tono: 'aviso', texto})
      },
    })
  }

  return (
    <div className="surface space-y-2 p-3" data-testid="ficha-ocr">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-0.5">
          <p className="inline-flex items-center gap-1.5 font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('admin.bateria.ficha.ocr.label')}
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('admin.bateria.ficha.ocr.label')})}
              testId="ficha-ocr-tip">
              {t('admin.bateria.ficha.ocr.tip')}
            </HelpIcon>
          </p>
        </div>
        <Button variant="outline" size="sm" disabled={ocr.isPending} onClick={lanzar} data-testid="ficha-ocr-boton">
          {ocr.isPending ? t('admin.bateria.ficha.ocr.leyendo') : t('admin.bateria.ficha.ocr.boton')}
        </Button>
      </div>
      {estado && (
        <p
          className={estado.tono === 'exito' ? 'text-sm text-foreground' : 'notice-amber rounded-md px-3 py-2 text-sm'}
          data-testid="ficha-ocr-estado">
          {estado.texto}
        </p>
      )}
    </div>
  )
}

/** Guardar nunca modifica al empleado, solo la ficha de esta evaluación. */
export function FichaDatosGenerales({
  businessId,
  evaluacionId,
  paqueteId,
  enabled,
  onSaved,
}: {
  businessId: string
  evaluacionId: string
  paqueteId: string
  enabled: boolean
  onSaved?: () => void
}) {
  const {t} = useTranslation()
  const {data: ficha} = useFicha(businessId, paqueteId, enabled)
  const save = useSaveFicha(businessId, evaluacionId, paqueteId)
  const [draft, setDraft] = useState<Draft>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (ficha) setDraft(draftFromCampos(ficha.catalogo, ficha.campos))
  }, [ficha])

  if (!ficha) return <Skeleton className="h-64 w-full" data-testid="ficha-skeleton" />

  const plan = construirPlan(ficha.catalogo)
  const principal = plan.filter((item) => itemAlimentaInforme(item, ficha.campos))
  const secundario = plan.filter((item) => !itemAlimentaInforme(item, ficha.campos))

  function submit() {
    setError(null)
    save.mutate(upsertFromDraft(ficha!.catalogo, draft), {
      onSuccess: () => onSaved?.(),
      onError: (err) => setError(translateApiError(err)),
    })
  }

  return (
    <div className="space-y-5" data-testid="ficha-datos-generales">
      <div className="flex flex-wrap items-baseline justify-end gap-3">
        {ficha.paquete_pdf_url && (
          <a
            href={ficha.paquete_pdf_url}
            target="_blank"
            rel="noreferrer"
            data-testid="ficha-ver-cuadernillo"
            className="text-xs text-primary hover:underline">
            {t('admin.bateria.cola.papel.cuadernillo')}
          </a>
        )}
      </div>

      <OcrTrigger businessId={businessId} paqueteId={paqueteId} />

      <section className="space-y-3" data-testid="ficha-impacto-principal">
        <p className="flex items-center gap-1.5 font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('admin.bateria.ficha.impacto.alimentan', {count: principal.length})}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('admin.bateria.ficha.impacto.alimentanLabel')})}
            testId="ficha-impacto-tip">
            {t('admin.bateria.ficha.impacto.help')}
          </HelpIcon>
        </p>
        <div className="space-y-4">
          {principal.map((item) => renderPlanItem(item, ficha, draft, setDraft, businessId))}
        </div>
      </section>

      {secundario.length > 0 && (
        <details className="surface p-3" data-testid="ficha-impacto-secundario">
          <summary
            className="cursor-pointer font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase"
            data-testid="ficha-otros-toggle">
            {t('admin.bateria.ficha.impacto.otros', {count: secundario.length})}
          </summary>
          <div className="mt-3 space-y-4">
            {secundario.map((item) => renderPlanItem(item, ficha, draft, setDraft, businessId))}
          </div>
        </details>
      )}

      {error && (
        <p className="text-sm text-destructive" data-testid="ficha-error">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <Button data-testid="ficha-guardar" disabled={save.isPending} onClick={submit}>
          {save.isPending ? t('common.saving') : t('admin.bateria.ficha.guardar')}
        </Button>
      </div>
    </div>
  )
}
